import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

export default function Signup() {
  const { register, error } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", role: "both" });
  const [agreedTos, setAgreedTos] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!agreedTos) return;
    setBusy(true);
    const ok = await register({ ...form, agreed_tos: true });
    setBusy(false);
    if (ok) { toast.success("Account created"); nav("/browse"); }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-neutral-200">
        <img src="https://images.unsplash.com/photo-1657757996603-acec063f1d9b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxlcmdvbm9taWMlMjBob21lJTIwd29ya3NwYWNlJTIwbWluaW1hbHxlbnwwfHx8fDE3ODY1MDI3MDl8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover"/>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <div className="label-eyebrow mb-2">Get started</div>
            <h1 className="font-display font-medium text-3xl tracking-tight">Create your DenShare account</h1>
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Name</div>
            <Input data-testid="signup-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="h-11 border-neutral-300"/>
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Email</div>
            <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="h-11 border-neutral-300"/>
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Password</div>
            <Input data-testid="signup-password" type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={6} className="h-11 border-neutral-300"/>
          </div>
          <div>
            <div className="label-eyebrow mb-1.5">Phone (optional)</div>
            <Input data-testid="signup-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="h-11 border-neutral-300"/>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="signup-error">{error}</div>}

          <label className="flex items-start gap-3 text-sm cursor-pointer select-none">
            <Checkbox
              data-testid="signup-tos-checkbox"
              checked={agreedTos}
              onCheckedChange={(v) => setAgreedTos(!!v)}
              className="mt-0.5 border-neutral-400 data-[state=checked]:bg-neutral-900 data-[state=checked]:border-neutral-900"
            />
            <span className="text-neutral-700 leading-relaxed">
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank" rel="noreferrer" className="underline text-neutral-900" data-testid="signup-tos-link">Terms of Service</Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer" className="underline text-neutral-900" data-testid="signup-privacy-link">Privacy Policy</Link>.
            </span>
          </label>

          <Button
            data-testid="signup-submit"
            type="submit"
            disabled={busy || !agreedTos}
            className="w-full h-11 bg-neutral-900 hover:bg-neutral-800 text-white rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Creating..." : "Create account"}
          </Button>
          <div className="text-sm text-neutral-500">
            Already have one? <Link to="/login" className="underline text-neutral-900">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
